import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ENVIRONMENT,
  LoadMessagesResponse,
  MarkMessagesAsReadRequest,
  MarkMessagesAsReadResponse,
  OriginationMessage,
  OriginationSendMessageRequest,
  RequestCallBackRequest,
  SendMessageResponse,
} from '@ld.consumer-portal/models';
import { firstValueFrom } from 'rxjs';
import { LoggingService } from '@ld.consumer-portal/utils';

@Injectable({
  providedIn: 'root',
})
export class OriginationMessagesService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly loggingService = inject(LoggingService);

  private get cpApiUrl(): string {
    return this.env.appSettings.cpApiUrl || '';
  }

  private get apiNamespace(): string {
    return this.env.appSettings.apiNamespace || 'api/v1';
  }

  async loadMessages(loanId: string, fromDate: Date): Promise<LoadMessagesResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/messages`;

    try {
      const res = await firstValueFrom(
        this.http.get<LoadMessagesResponse>(url, {
          params: { fromDate: fromDate.toISOString() },
        })
      );
      return { ...res, fromDate };
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error loading messages for loan ${loanId}`);
      throw new Error(`Failed to load messages: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async sendMessage(loanId: string, request: OriginationSendMessageRequest): Promise<SendMessageResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/messages`;

    try {
      const message = await firstValueFrom(this.http.post<OriginationMessage>(url, request));
      return { message, trackId: request.trackId };
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error sending message for loan ${loanId}`);
      throw new Error(`Failed to send message: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async markMessagesAsRead(loanId: string, request: MarkMessagesAsReadRequest): Promise<MarkMessagesAsReadResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/messages/read`;

    try {
      return await firstValueFrom(this.http.put<MarkMessagesAsReadResponse>(url, request));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error marking messages as read for loan ${loanId}`);
      throw new Error(`Failed to mark messages as read: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async requestCallBack(loanId: string, borrowerId: string, request: RequestCallBackRequest): Promise<void> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/borrowers/${borrowerId}/callback`;

    try {
      await firstValueFrom(this.http.post<void>(url, request));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error requesting callback for loan ${loanId}, borrower ${borrowerId}`);
      throw new Error(`Failed to request callback: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
}
